import { getAudioContext } from './audio-context';

export type StopAudioTimerFunction = () => void;
export type AudioTimerCallback = (currentTime: number, stop: StopAudioTimerFunction) => void;

const audioContext = getAudioContext();
let silenceGainNode: GainNode | null = null;

if (audioContext) {
    // Chrome needs our oscillator node to be attached to the destination
    // So we create a silent Gain Node
    silenceGainNode = audioContext.createGain();
    silenceGainNode.gain.value = 0;
    silenceGainNode.connect(audioContext.destination);
}

/**
 * @param {Function} callback
 * @param {number} interval {number}
 *
 * @returns {Function}
 */
export function setAudioInterval(callback: AudioTimerCallback, interval: number): StopAudioTimerFunction {
    const freq = interval / 1000; // AudioContext time parameters are in seconds

    let isStopped = false; // A flag to know when we'll stop the loop
    const stop = () => {
        isStopped = true;
    };

    const onOscillatorEnd = () => {
        if (!audioContext || !silenceGainNode) {
            return;
        }

        const osc = audioContext.createOscillator();
        osc.onended = () => {
            if (silenceGainNode) {
                osc.disconnect(silenceGainNode);
            }

            if (!isStopped) {
                callback(audioContext.currentTime, stop); // one frame is done
                onOscillatorEnd();
            }
        }; // so we can loop

        osc.connect(silenceGainNode);
        osc.start(0); // start it now
        osc.stop(audioContext.currentTime + freq); // stop it next frame
    };

    onOscillatorEnd();

    // return a function to stop our loop
    return stop;
}

/**
 * @param {Function} callback
 * @param {number} interval {number}
 *
 * @returns {Function}
 */
export function setAudioTimeout(callback: AudioTimerCallback, interval: number): StopAudioTimerFunction {
    return setAudioInterval((currentTime, stop) => {
        stop();
        callback(currentTime, stop);
    }, interval);
}
